'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import {
  PulseButton,
  PulseIconButton,
  CircularProgress,
  Search,
  Select,
  Tooltip,
} from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlus, faCircle, faRotateRight } from '@fortawesome/pro-solid-svg-icons';
import { PulseHeader, PulseCell } from 'src/components/PulseUI/PulseTable';
import type { PulseColumnDef } from 'src/components/PulseUI/PulseTable';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import {
  InternalViewBadge,
  PageHeader,
} from 'src/components/Settings/components';
import { useDebounce } from 'use-debounce';
import { CompanyTableExportMenu } from '../CompanyTableExportMenu';
import { ReactivateCompanyDialog } from '../ReactivateCompanyDialog';
import {
  useGetAllCompaniesQuery,
  useSearchCompaniesAndUsersQuery,
} from 'src/api/redux';

interface SearchResultItem {
  id: string;
  label: string;
  type: 'company' | 'user';
  company_id?: string;
}

type CompanyStatus = 'active' | 'inactive' | 'all';

type HubSpotLinkFilter = 'all' | 'linked' | 'unlinked';

interface Company {
  id: string | number;
  name: string;
  /** Active *and* signed in at least once — narrower than the column shows. */
  active_user_count: number;
  /**
   * Everyone who can sign in, invited users included. This is the Active Users
   * column. Optional because an API build predating MKT-445 omits it.
   */
  total_active_user_count?: number;
  invited_user_count?: number;
  inactive_user_count?: number;
  user_count: number;
  deleted_at: string | null;
  hubspot_company_id?: string | null;
  hubspot_company_name?: string | null;
  hubspot_domain?: string | null;
}

/**
 * Companies are fetched in a single page and revealed as the viewer scrolls,
 * matching the company user table. 1000 is the API's
 * `DefaultPagination.max_page_size`.
 */
const COMPANIES_REQUEST_PAGE_SIZE = 1000;

/** Rows revealed at a time as the viewer scrolls. */
const ROWS_PER_BATCH = 50;

/** Distance from the bottom of the scroll container that reveals the next batch. */
const SCROLL_THRESHOLD_PX = 50;

const COLUMNS: PulseColumnDef[] = [
  { field: 'status', headerName: 'Active', width: 80, resizable: false, sortable: false },
  { field: 'name', headerName: 'Company Name', width: 300, sortable: true },
  // Every user who can sign in — `active_user_count` now excludes invited users
  // (MKT-445), so the total is what keeps this column reading as it always has.
  { field: 'total_active_user_count', headerName: 'Active Users', width: 150, sortable: false },
  { field: 'user_count', headerName: 'Total Users', width: 150, sortable: false },
  { field: 'hubspot', headerName: 'HubSpot', width: 220, sortable: false },
  { field: 'actions', headerName: '', width: 80, resizable: false, sortable: false },
];

/**
 * @description   CompanyTable component is used to display the company table in the admin dashboard
 * @returns       CompanyTable component
 */
const CompanyTable = () => {
  const router = useRouter();
  const { setShowAddEditCompanyModal, setshowAddDemoEnvModal } = useDashboard();
  const wasResizingRef = useRef(false);

  const [sort, setSort] = useState<{ sort: string; order: 'asc' | 'desc' }>({
    sort: 'name',
    order: 'asc',
  });
  // Rows are revealed in batches as the viewer scrolls rather than paged.
  const [visibleCount, setVisibleCount] = useState(ROWS_PER_BATCH);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [status, setStatus] = useState<CompanyStatus>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const { data: search, isLoading: searchLoading } =
    useSearchCompaniesAndUsersQuery(debouncedSearchTerm, {
      skip: !debouncedSearchTerm || debouncedSearchTerm.length < 3,
    });

  const { data, isLoading, isFetching } = useGetAllCompaniesQuery({
    ...sort,
    page: 1,
    pageSize: COMPANIES_REQUEST_PAGE_SIZE,
    status,
  });

  const [hubSpotFilter, setHubSpotFilter] = useState<HubSpotLinkFilter>('all');

  const loadedCompanies: Company[] = (data?.results ?? []) as Company[];
  const isTruncated = (data?.count ?? 0) > loadedCompanies.length;
  // Filtered client-side: every company arrives in one request.
  const filteredCompanies =
    hubSpotFilter === 'all'
      ? loadedCompanies
      : loadedCompanies.filter(
          (company) =>
            !!company?.hubspot_company_id === (hubSpotFilter === 'linked')
        );
  const totalCompanies =
    hubSpotFilter === 'all' ? data?.count ?? 0 : filteredCompanies.length;
  const visibleCompanies = filteredCompanies.slice(0, visibleCount);
  const hasMoreToReveal = visibleCount < filteredCompanies.length;

  // Reveal the next batch as the viewer nears the bottom of the scroll area.
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasMoreToReveal) return;

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

      if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD_PX) {
        setVisibleCount((previous) => previous + ROWS_PER_BATCH);
      }
    },
    [hasMoreToReveal]
  );

  // Start again from the first batch when the result set changes underneath the
  // viewer, and scroll them back to the top of the new list.
  useEffect(() => {
    setVisibleCount(ROWS_PER_BATCH);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [sort, status, hubSpotFilter]);

  const [reactivateTarget, setReactivateTarget] = useState<Company | null>(null);

  const pulseColumns = COLUMNS.map((col) => ({
    ...col,
    width: col.width ?? 150,
    minWidth: col.minWidth ?? 80,
    maxWidth: col.maxWidth ?? 600,
    resizable: col.resizable !== false,
  }));

  const { startResizing, isResizing, resizingField, getColumnWidth } =
    usePulseTableColumns({ columns: pulseColumns });

  // Build grid template: name column stretches, others use hook-managed widths (persist after resize)
  const getGridTemplateColumns = () => {
    return COLUMNS.map((col) => {
      if (col.field === 'name') return 'minmax(200px, 1fr)';
      if (resizingField === col.field) {
        const width = getColumnWidth(col.field) ?? col.width ?? 150;
        return `var(--pulse-col-${col.field}, ${width}px)`;
      }
      return `${getColumnWidth(col.field) ?? col.width ?? 150}px`;
    }).join(' ');
  };

  const handleSortChange = (field: string) => {
    if (wasResizingRef.current) return;
    const newOrder =
      sort.sort === field && sort.order === 'asc' ? 'desc' : 'asc';
    setSort({ sort: field, order: newOrder });
  };

  const handleOpenDialog = () => {
    setShowAddEditCompanyModal(true);
  };

  const handleShowDemoEnvDialog = () => {
    setshowAddDemoEnvModal(true);
  };

  const handleRowClick = (company: Company) => {
    if (isResizing) return;
    if (company.deleted_at) return;
    router.push(`/settings/company/${company?.id}`);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchResultClick = (item: SearchResultItem) => {
    if (!item) return;
    if (item.type === 'company') {
      router.push(`/settings/company/${item.id}`);
    } else if (item.type === 'user') {
      router.push(`/settings/company/${item.company_id}/users/${item.id}`);
    }
  };

  const handleSearchResultKeyDown = (
    e: React.KeyboardEvent,
    item: SearchResultItem
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSearchResultClick(item);
    }
  };

  const renderCell = (company: Company, col: PulseColumnDef) => {
    switch (col.field) {
      case 'status':
        return (
          <>
            <Icon
              icon={faCircle}
              className={`h-3.5 w-3.5 ${
                company.deleted_at ? 'text-red-500' : 'text-green-500'
              }`}
              aria-hidden="true"
            />
            <span className="sr-only">
              {company.deleted_at ? 'Inactive' : 'Active'}
            </span>
          </>
        );
      case 'name':
        return (
          <span className="truncate" data-testid={`company-name-${company.id}`}>
            {company.name}
          </span>
        );
      case 'total_active_user_count':
        // An API build predating MKT-445 has no total, and its
        // `active_user_count` still means everyone who can sign in — the same
        // figure this column has always shown.
        return (
          <span>
            {company.total_active_user_count ?? company.active_user_count}
          </span>
        );
      case 'user_count':
        return <span>{company.user_count}</span>;
      case 'hubspot':
        if (!company.hubspot_company_id) {
          return (
            <span
              className="text-amber-700"
              data-testid={`company-hubspot-unlinked-${company.id}`}
            >
              Unlinked
            </span>
          );
        }
        return (
          <div
            className="flex flex-col min-w-0"
            data-testid={`company-hubspot-linked-${company.id}`}
          >
            <span className="truncate">{company.hubspot_company_name}</span>
            {company.hubspot_domain && (
              <span className="truncate text-xs text-neutral-500">
                {company.hubspot_domain}
              </span>
            )}
          </div>
        );
      case 'actions':
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {company.deleted_at ? (
              <Tooltip content="Reactivate company">
                <div>
                  <PulseIconButton
                    size="sm"
                    variant="plain"
                    color="success"
                    icon={faRotateRight}
                    aria-label={`Reactivate ${company.name}`}
                    data-testid={`reactivate-company-${company.id}`}
                    onClick={() => setReactivateTarget(company)}
                  />
                </div>
              </Tooltip>
            ) : (
              <CompanyTableExportMenu
                table="companies"
                companyId={String(company.id)}
              />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="flex flex-col gap-6 h-[calc(100vh-12rem-200px)] px-12 sm:px-20"
      data-testid="company-table"
    >
      <PageHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex flex-col flex-1">
            {/* This whole page is superuser-only, so the badge is unconditional. */}
            <div className="flex flex-wrap items-center gap-2">
              <h2
                className="text-lg font-semibold text-neutral-900"
                data-testid="company-table-header"
              >
                Companies
              </h2>
              <InternalViewBadge />
            </div>
            <span className="text-xs text-neutral-500 font-semibold">
              List of all companies in the system.
            </span>
          </div>
          <div className="flex-shrink-0" data-testid="company-status-filter">
            <Select
              value={status}
              onChange={(_, value) => setStatus(value as CompanyStatus)}
              className="w-44 rounded-full"
              aria-label="Filter companies by status"
              variant="outlined"
              size="sm"
              data-testid="company-status-select"
            >
              <option value="active">Active Companies</option>
              <option value="inactive">Inactive Companies</option>
              <option value="all">All Companies</option>
            </Select>
          </div>
          <div className="flex-shrink-0" data-testid="company-hubspot-filter">
            <Select
              value={hubSpotFilter}
              onChange={(_, value) => setHubSpotFilter(value as HubSpotLinkFilter)}
              className="w-44 rounded-full"
              aria-label="Filter companies by HubSpot link"
              variant="outlined"
              size="sm"
              data-testid="company-hubspot-select"
            >
              <option value="all">Any HubSpot Link</option>
              <option value="linked">Linked to HubSpot</option>
              <option value="unlinked">Not Linked</option>
            </Select>
          </div>
          <div className="relative flex flex-1 max-w-sm items-center gap-2">
            <Search
              value={searchTerm}
              onSearch={handleSearch}
              placeholder="Search Companies and Users"
              className="w-full rounded-full bg-white"
              aria-label="Search companies and users"
              data-testid="company-table-search"
            />
            {searchTerm.length > 2 && (
              <div
                className="absolute top-full mt-1 w-full max-h-96 overflow-y-auto bg-white rounded-lg shadow-lg border border-neutral-200 z-50"
                role="listbox"
                aria-label="Search results"
                data-testid="company-table-search-results"
              >
                {searchLoading ? (
                  <div
                    className="p-4 text-center text-neutral-500"
                    role="status"
                    aria-live="polite"
                    data-testid="company-table-search-loading"
                  >
                    <CircularProgress size="sm" color="neutral" />
                    <span className="sr-only">Loading search results...</span>
                  </div>
                ) : search?.length > 0 ? (
                  <ul role="listbox">
                    {(search as SearchResultItem[]).map(
                      (item: SearchResultItem, idx: number) => (
                        <li
                          key={item.id || idx}
                          role="option"
                          tabIndex={0}
                          aria-selected={false}
                          className="px-4 py-3 hover:bg-neutral-50 cursor-pointer transition-colors duration-150"
                          onClick={() => handleSearchResultClick(item)}
                          onKeyDown={(e) => handleSearchResultKeyDown(e, item)}
                          data-testid={`company-table-search-result-${idx}`}
                        >
                          <span className="text-sm text-neutral-700">
                            {item.label}
                          </span>
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <div
                    className="p-4 text-center text-neutral-500"
                    role="status"
                    data-testid="company-table-search-no-results"
                  >
                    No results found.
                  </div>
                )}
              </div>
            )}
          </div>
          <PulseButton
            size="sm"
            variant="outline"
            color="neutral"
            onClick={handleShowDemoEnvDialog}
            className="rounded-full"
            startDecorator={faPlus}
            aria-label="Add demo company"
            data-testid="company-table-add-demo-btn"
          >
            Add Demo Company
          </PulseButton>
          <PulseButton
            size="sm"
            variant="solid"
            color="primary"
            onClick={handleOpenDialog}
            className="rounded-full"
            startDecorator={faPlus}
            aria-label="Add new company"
            data-testid="company-table-add-company-btn"
          >
            Add Company
          </PulseButton>
        </div>
      </PageHeader>
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 h-full flex flex-col">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-auto relative"
          data-testid="company-table-scroll-container"
        >
          {isLoading && !data ? (
            <div
              className="flex items-center justify-center h-full"
              role="status"
              aria-live="polite"
              data-testid="company-table-loading"
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              {isFetching && data && (
                <div
                  className="absolute inset-0 z-20 bg-white/60 flex items-center justify-center"
                  role="status"
                  aria-live="polite"
                  data-testid="company-table-refetch-overlay"
                >
                  <CircularProgress size="sm" color="neutral" />
                </div>
              )}
              <div
                data-testid="company-pulse-table"
                role="grid"
                className="grid w-full"
                style={{
                  gridTemplateColumns: getGridTemplateColumns(),
                }}
              >
                {/* Header row */}
                {COLUMNS.map((col) => {
                  const isSorted = sort.sort === col.field;
                  const pulseCol = pulseColumns.find((p) => p.field === col.field);
                  const colResizable = pulseCol?.resizable !== false;
                  return (
                    <div
                      key={col.field}
                      className="sticky top-0 z-10 bg-neutral-50 border-b border-neutral-200"
                      role={col.sortable ? 'button' : undefined}
                      tabIndex={col.sortable ? 0 : undefined}
                      onClick={() => {
                        if (col.sortable) handleSortChange(col.field);
                      }}
                      onKeyDown={(e) => {
                        if (col.sortable && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleSortChange(col.field);
                        }
                      }}
                    >
                      <PulseHeader
                        label={col.headerName}
                        sortable={col.sortable === true}
                        sortDirection={isSorted ? sort.order : null}
                        className={col.sortable ? 'cursor-pointer' : ''}
                        resizable={colResizable}
                        onStartResize={
                          colResizable
                            ? (e) => startResizing(col.field, e)
                            : undefined
                        }
                        isResizing={resizingField === col.field}
                      />
                    </div>
                  );
                })}

                {/* Data rows */}
                {visibleCompanies.map((company: Company) => (
                  <div
                    key={company.id}
                    className={`group contents ${company.deleted_at ? 'cursor-default' : 'cursor-pointer'}`}
                    role="row"
                    tabIndex={company.deleted_at ? undefined : 0}
                    onClick={() => handleRowClick(company)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !company.deleted_at) {
                        e.preventDefault();
                        handleRowClick(company);
                      }
                    }}
                    data-testid={`company-table-row-${company.id}`}
                  >
                    {COLUMNS.map((col) => (
                      <PulseCell
                        key={col.field}
                        className="border-b border-neutral-100 transition-colors group-hover:bg-neutral-50"
                      >
                        <div
                          className="w-full h-full flex items-center"
                          onClick={
                            col.field === 'actions'
                              ? (e) => e.stopPropagation()
                              : undefined
                          }
                        >
                          {renderCell(company, col)}
                        </div>
                      </PulseCell>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div
          className="flex-none bg-white px-4 py-3 flex items-center justify-end gap-2 border-t border-neutral-200 sm:px-6"
          data-testid="company-table-count"
        >
          <p
            className="text-xs text-neutral-600"
            role="status"
            aria-live="polite"
          >
            {`${totalCompanies} ${
              totalCompanies === 1 ? 'company' : 'companies'
            }`}
          </p>
          {isTruncated && (
            <Tooltip content="This list has more companies than one request returns. Use search to find the rest.">
              <span
                className="text-xs text-neutral-500"
                data-testid="company-table-truncated"
              >
                {/* Filtering only sees loaded rows, so a filtered count is
                    partial — say so rather than imply a total. */}
                {hubSpotFilter === 'all'
                  ? `· first ${loadedCompanies.length} loaded`
                  : `· among the first ${loadedCompanies.length} loaded`}
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      <ReactivateCompanyDialog
        open={!!reactivateTarget}
        onClose={() => setReactivateTarget(null)}
        companyId={String(reactivateTarget?.id ?? '')}
        companyName={reactivateTarget?.name ?? ''}
      />
    </div>
  );
};

export default CompanyTable;
